import { formatCurrency } from '@/utils/format';
import type { OrderItemWithDetails } from '@/types/common';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Modal from '@/components/common/Modal';

interface OrderSummaryProps {
  items: OrderItemWithDetails[];
  onQuantityChange: (item: OrderItemWithDetails, newQuantity: number) => Promise<void>;
  onProceedToPayment: () => void;
  saving: boolean;
  isDesktop?: boolean;
  onDeleteOrder?: () => Promise<void>;
  tableNumber?: number;
}

export default function OrderSummary({
  items,
  onQuantityChange,
  onProceedToPayment,
  saving,
  isDesktop = false,
  onDeleteOrder,
  tableNumber
}: OrderSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Calculate total
  const total = items.reduce((sum, item) => sum + (item.menu_item.price * item.quantity), 0);

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (onDeleteOrder) {
      await onDeleteOrder();
    }
    setIsDeleteModalOpen(false);
  };

  // Only render mobile content if not desktop
  if (!isDesktop) {
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 sm:hidden z-50">
          {/* Mobile Header/Toggle */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full bg-white dark:bg-dark-bg-light px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-dark-border"
          >
            <div className="flex items-center space-x-2">
              <i className="fas fa-receipt text-primary-600 dark:text-primary-400" />
              <span className="font-medium">Order Summary</span>
              {tableNumber && (
                <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
                  (Table #{tableNumber})
                </span>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </span>
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'up'} text-gray-500`} />
            </div>
          </button>

          {/* Expandable Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="bg-white dark:bg-dark-bg-light border-t border-gray-200 dark:border-dark-border overflow-hidden"
              >
                <div className="p-4 max-h-[60vh] overflow-y-auto">
                  {/* Order Items */}
                  <div className="space-y-4 mb-4">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-bg rounded-lg"
                      >
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-dark-text-primary">
                            {item.menu_item.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                            €{item.menu_item.price.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => onQuantityChange(item, item.quantity - 1)}
                            disabled={saving}
                            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            <i className="fas fa-minus" />
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => onQuantityChange(item, item.quantity + 1)}
                            disabled={saving}
                            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            <i className="fas fa-plus" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">Total</span>
                      <span className="text-xl font-semibold text-primary-600 dark:text-primary-400">
                        €{total.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <button
                      onClick={onProceedToPayment}
                      disabled={saving}
                      className="w-full inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-lg transition-all duration-300 ease-out bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_4px_0_0_#0369a1] hover:shadow-[0_6px_0_0_#0369a1] hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-[0_2px_0_0_#0369a1] touch-manipulation disabled:opacity-50"
                    >
                      <i className="fas fa-credit-card mr-2" />
                      Proceed to Payment
                    </button>
                    {onDeleteOrder && (
                      <button
                        onClick={handleDeleteClick}
                        className="w-full py-2 px-4 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50"
                      >
                        Delete Order
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Order"
          actions={
            <>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Delete
              </button>
            </>
          }
        >
          <p className="text-gray-600 dark:text-dark-text-secondary">
            Are you sure you want to delete this order? This action cannot be undone.
          </p>
        </Modal>
      </>
    );
  }

  // Desktop view
  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white dark:bg-dark-bg-light border-b border-gray-200 dark:border-dark-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i className="fas fa-receipt text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">Order Summary</h2>
            {tableNumber && (
              <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
                (Table #{tableNumber})
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary">
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </span>
            {onDeleteOrder && (
              <button
                onClick={handleDeleteClick}
                className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title="Delete Order"
              >
                <i className="fas fa-trash-alt" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Items List */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 p-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-bg rounded-lg"
            >
              <div className="flex-1">
                <h4 className="text-base font-medium text-gray-900 dark:text-dark-text-primary">
                  {item.menu_item.name}
                </h4>
                <p className="text-xs text-gray-600 dark:text-dark-text-secondary">
                  €{item.menu_item.price.toFixed(2)}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => onQuantityChange(item, item.quantity - 1)}
                  disabled={saving}
                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  <i className="fas fa-minus" />
                </button>
                <span className="w-8 text-center text-sm">{item.quantity}</span>
                <button
                  onClick={() => onQuantityChange(item, item.quantity + 1)}
                  disabled={saving}
                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  <i className="fas fa-plus" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-light">
        <div className="p-4 space-y-4">
          {/* Total */}
          <div className="p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-base font-medium text-gray-900 dark:text-dark-text-primary">Total</span>
              <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                €{total.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Proceed to Payment Button */}
          <button
            onClick={onProceedToPayment}
            disabled={saving}
            className="w-full inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-300 ease-out bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_4px_0_0_#0369a1] hover:shadow-[0_6px_0_0_#0369a1] hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-[0_2px_0_0_#0369a1] touch-manipulation disabled:opacity-50"
          >
            <i className="fas fa-credit-card mr-2" />
            Proceed to Payment
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Order"
        actions={
          <>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Are you sure you want to delete this order? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
}